import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { teamService, TeamMemberRecord, InvitableRole } from '@/services/team';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/skeletons';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertCircle, Loader2, Mail, Pencil, Plus, Trash2, Users,
} from 'lucide-react';
import toastService from '@/services/toast/service';
import { useIsViewer, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer';

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Admin',
  viewer: 'Viewer',
  internal_viewer: 'Internal Viewer',
};

const ROLE_DESCRIPTIONS: Record<InvitableRole, string> = {
  viewer: 'Can browse everything and open client accounts via shadow access, but cannot change or delete backoffice data.',
  internal_viewer: 'Same read-only access to the backoffice, but never gets into a client account: shadow access skips them entirely.',
};

const TeamPage = () => {
  const { user } = useAuth();
  const isViewer = useIsViewer();
  const columnCount = 5;

  const [members, setMembers] = useState<TeamMemberRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<InvitableRole>('viewer');
  const [inviting, setInviting] = useState(false);

  // Edit role modal
  const [editTarget, setEditTarget] = useState<TeamMemberRecord | null>(null);
  const [editRole, setEditRole] = useState<InvitableRole>('viewer');
  const [savingRole, setSavingRole] = useState(false);

  // Remove confirmation
  const [removeTarget, setRemoveTarget] = useState<TeamMemberRecord | null>(null);
  const [removing, setRemoving] = useState(false);

  // Pending-invite resend busy state
  const [resendingId, setResendingId] = useState<string | null>(null);

  const loadTeam = useCallback(async () => {
    try {
      setError(null);
      const data = await teamService.getTeam();
      setMembers(data);
    } catch {
      setError("Couldn't load the team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await teamService.invite(inviteEmail.trim(), inviteRole);
      toastService.success('Invitation sent');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('viewer');
      await loadTeam();
    } catch (err: any) {
      // The api interceptor already toasts server-provided messages
      if (!err.response?.data?.message) toastService.error("Couldn't send the invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (member: TeamMemberRecord) => {
    setResendingId(member.id);
    try {
      await teamService.resendInvite(member.id);
      toastService.success('Invitation resent');
    } catch (err: any) {
      if (!err.response?.data?.message) toastService.error("Couldn't resend the invitation");
    } finally {
      setResendingId(null);
    }
  };

  const openEdit = (member: TeamMemberRecord) => {
    setEditRole(member.role === 'internal_viewer' ? 'internal_viewer' : 'viewer');
    setEditTarget(member);
  };

  const handleSaveRole = async () => {
    if (!editTarget || editRole === editTarget.role) return;
    setSavingRole(true);
    try {
      await teamService.updateRole(editTarget.id, editRole);
      toastService.success('Role updated');
      setEditTarget(null);
      await loadTeam();
    } catch (err: any) {
      if (!err.response?.data?.message) toastService.error("Couldn't update the role");
    } finally {
      setSavingRole(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await teamService.remove(removeTarget.id);
      toastService.success(removeTarget.status === 'pending' ? 'Invitation cancelled' : 'Team member removed');
      setRemoveTarget(null);
      await loadTeam();
    } catch (err: any) {
      if (!err.response?.data?.message) toastService.error("Couldn't remove the team member");
    } finally {
      setRemoving(false);
    }
  };

  const isSelf = (member: TeamMemberRecord) => member.status === 'active' && member.id === user?.id;

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
          Team{' '}
          <span className="text-muted-foreground font-normal">({members.length})</span>
        </h1>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="w-full sm:w-auto">
              <Button onClick={() => setInviteOpen(true)} disabled={isViewer} className="w-full sm:w-auto">
                <Plus />
                Invite Member
              </Button>
            </span>
          </TooltipTrigger>
          {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
        </Tooltip>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Card container for table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">Name</TableHead>
                <TableHead className="w-[180px] text-center">Email</TableHead>
                <TableHead className="w-[140px] text-center">Role</TableHead>
                <TableHead className="w-[100px] text-center">Status</TableHead>
                <TableHead className="w-[100px] text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className={loading ? 'pointer-events-none' : ''}>
              {loading ? (
                <TableSkeleton columns={columnCount} rows={3} />
              ) : members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} className="h-48 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Users className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">No team members yet</h3>
                      <p className="text-sm text-muted-foreground">
                        Invite your first team member to help manage the backoffice.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={`${member.status}-${member.id}`}>
                    <TableCell>
                      <span className="font-medium">{member.name && member.name.trim() ? member.name : 'Unknown'}</span>
                      {isSelf(member) && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {member.email || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {ROLE_LABELS[member.role] ?? member.role}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge tone={member.status === 'active' ? 'success' : 'warning'}>
                        {member.status === 'active' ? 'Active' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center">
                        <div className="inline-flex items-center rounded-md border">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-none rounded-l-md border-r"
                                  disabled={isSelf(member) || isViewer || member.role === 'super_admin'}
                                  onClick={() => openEdit(member)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {isViewer
                                ? VIEWER_NO_PERMISSION
                                : isSelf(member)
                                  ? 'You cannot change your own role'
                                  : member.role === 'super_admin'
                                    ? "An admin's role cannot be changed"
                                    : 'Edit Role'}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`h-8 w-8 rounded-none hover:bg-destructive/10 hover:text-destructive ${member.status === 'pending' ? 'border-r' : 'rounded-r-md'}`}
                                  disabled={isSelf(member) || isViewer}
                                  onClick={() => setRemoveTarget(member)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {isViewer
                                ? VIEWER_NO_PERMISSION
                                : isSelf(member)
                                  ? 'You cannot remove yourself'
                                  : member.status === 'pending' ? 'Cancel Invitation' : 'Remove'}
                            </TooltipContent>
                          </Tooltip>
                          {member.status === 'pending' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span tabIndex={0}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 rounded-none rounded-r-md"
                                    disabled={resendingId === member.id || isViewer}
                                    onClick={() => handleResend(member)}
                                  >
                                    {resendingId === member.id
                                      ? <Loader2 className="h-4 w-4 animate-spin" />
                                      : <Mail className="h-4 w-4" />}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : 'Resend Invite'}</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Invite modal */}
      <Dialog open={inviteOpen} onOpenChange={(open) => !inviting && setInviteOpen(open)}>
        <DialogContent>
          <form onSubmit={(e) => { e.preventDefault(); handleInvite(); }}>
            <DialogHeader>
              <DialogTitle>Invite Member</DialogTitle>
              <DialogDescription>
                Send an invitation to join the backoffice. Use a dedicated staff
                email, addresses with an existing customer account can't be invited.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="name@roasform.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  disabled={inviting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select
                  value={inviteRole}
                  onValueChange={(value) => setInviteRole(value as InvitableRole)}
                  disabled={inviting}
                >
                  <SelectTrigger id="invite-role" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="internal_viewer">Internal Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-lg border bg-muted/50 p-3">
                <p className="text-sm text-muted-foreground">
                  <strong>{ROLE_LABELS[inviteRole]} permissions:</strong>{' '}
                  {ROLE_DESCRIPTIONS[inviteRole]}
                </p>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setInviteOpen(false)} disabled={inviting}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviting || !inviteEmail.trim()}>
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {inviting ? 'Sending...' : 'Invite'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit role modal */}
      <Dialog open={!!editTarget} onOpenChange={(open) => !savingRole && !open && setEditTarget(null)}>
        <DialogContent>
          <form onSubmit={(e) => { e.preventDefault(); handleSaveRole(); }}>
            <DialogHeader>
              <DialogTitle>Edit Member</DialogTitle>
              <DialogDescription>
                {editTarget?.name && editTarget.name.trim() ? editTarget.name : editTarget?.email}
                {editTarget?.status === 'pending' && ' has not accepted their invitation yet, so the new role applies when they do.'}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select
                  value={editRole}
                  onValueChange={(value) => setEditRole(value as InvitableRole)}
                  disabled={savingRole}
                >
                  <SelectTrigger id="edit-role" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="internal_viewer">Internal Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-lg border bg-muted/50 p-3">
                <p className="text-sm text-muted-foreground">
                  <strong>{ROLE_LABELS[editRole]} permissions:</strong>{' '}
                  {ROLE_DESCRIPTIONS[editRole]}
                </p>
              </div>
              {editTarget?.status === 'active' && editRole !== editTarget.role && (
                <p className="text-sm text-muted-foreground">
                  {editRole === 'internal_viewer'
                    ? 'Saving removes their access to client accounts and signs them out everywhere.'
                    : 'Saving gives them the shadow access the rest of the team already has.'}
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setEditTarget(null)} disabled={savingRole}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingRole || editRole === editTarget?.role}>
                {savingRole ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {savingRole ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && !removing && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {removeTarget?.status === 'pending' ? 'Cancel invitation?' : 'Remove team member?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.status === 'pending'
                ? `The invitation to ${removeTarget?.email} will no longer be usable.`
                : `The staff account for ${removeTarget?.email} will be deleted, removing access to the backoffice and every client account.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleRemove} disabled={removing}>
              {removing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {removeTarget?.status === 'pending' ? 'Cancel invitation' : 'Remove'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamPage;
